import type { CompleteDateRange } from "../stores/structured-search/structured-search-store";

import type { VersionedEntityConfig } from "./versioned-entity-config";
import type {
  Filter,
  FilterValue,
  OperatorDefinition,
  StructuredFilterGroupRequest,
} from "./types";

/**
 * Semantic value type of a filter. This is intentionally decoupled from the
 * backend field type / operator set: `FilterValueType` only governs how a
 * value is rendered, parsed/validated, and formatted. The accepted operators
 * are declared explicitly per {@link FilterDefinition}.
 *
 * Centralizing behavior by `type` (rather than per-filter callbacks) is the
 * core idea of the schema-driven search engine.
 */
export enum FilterValueType {
  String = "string",
  Number = "number",
  Cost = "cost",
  Latency = "latency",
  Date = "date",
  DateRange = "dateRange",
  Enum = "enum",
  Nested = "nested",
  Prompt = "prompt",
  Boolean = "boolean",
}

export interface FilterEnumOption {
  label: string;
  value: string;
}

export enum SuggestionSourceType {
  Static = "static",
  Aggregation = "aggregation",
  Nested = "nested",
  PromptRegistry = "promptRegistry",
  WorkflowRegistry = "workflowRegistry",
}

export enum ValueDisplayType {
  Default = "default",
  RequestStatus = "requestStatus",
  TraceStatus = "traceStatus",
  Provider = "provider",
  Intent = "intent",
}

export type FilterSuggestionConfig =
  | { source: SuggestionSourceType.Static }
  | { source: SuggestionSourceType.Aggregation; field?: string }
  | { source: SuggestionSourceType.Nested; keysField: string; valuesField: string }
  | { source: SuggestionSourceType.PromptRegistry }
  | { source: SuggestionSourceType.WorkflowRegistry };

/**
 * A single filterable field. Owns everything the generic engine needs to
 * render its builder steps, suggest values, validate/format, and compile to a
 * backend filter clause. Keys should be backend-native wherever possible so
 * the generic compiler can emit `{ field: key, operator, value, nestedKey }`
 * without custom mapping.
 */
export interface FilterDefinition {
  key: string;
  label: string;
  type: FilterValueType;
  operators: OperatorDefinition[];
  group?: string;
  /** Keyword aliases that resolve to this filter in the field matcher. */
  aliases?: string[];
  /** Declares how the value step resolves suggestions for this filter. */
  suggestions?: FilterSuggestionConfig;
  /** Optional presentation override for enum/string values (status badges, provider logos, etc.). */
  valueDisplay?: ValueDisplayType;
  /** Options for {@link FilterValueType.Enum} (and static suggestions). */
  options?: FilterEnumOption[];
  unit?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  description?: string;
  /** Two-step entity → version selection for trace prompt/workflow filters. */
  versionedEntity?: VersionedEntityConfig;
}

/** First-class search controls rendered beside the filter list. */
export enum GlobalSearchControlType {
  DateRange = "dateRange",
  Timezone = "timezone",
  FreeTextQuery = "q",
  CutoverDate = "cutoverDate",
}

export interface GlobalSearchControl {
  type: GlobalSearchControlType;
  /**
   * Backend field a control compiles to, when applicable (e.g. the date-range
   * control compiles to `request_start_time`).
   */
  field?: string;
}

/** Snapshot of all generic search state a domain adapter compiles from. */
export interface StructuredSearchState {
  filters: Filter[];
  dateRange: CompleteDateRange;
  q: string;
}

export interface ValueSuggestionsHookArgs {
  field: string | undefined;
  operator: string | undefined;
  input: string;
  enabled: boolean;
  searchType: string;
  filterGroup?: StructuredFilterGroupRequest;
  metadataKey?: string;
  promptId?: number;
}

export interface ValueSuggestionOption {
  value: string;
  label?: string;
  count?: number;
  prompt_name?: string;
}

export interface ValueSuggestionsHookResult {
  /** `null` means "not handled by this provider" (fall back to other sources). */
  options: ValueSuggestionOption[] | null;
  isLoading: boolean;
}

/**
 * Everything domain-specific the generic structured-search engine needs. A
 * schema is provided per {@link StructuredSearchProvider}; Request Logs and
 * Traces each supply their own instance so a single component tree powers both.
 */
export interface StructuredSearchSchema {
  id: string;
  placeholder: string;
  compactPlaceholder: string;
  filters: FilterDefinition[];
  globalControls: GlobalSearchControl[];
  buildPayload: (state: StructuredSearchState) => StructuredFilterGroupRequest;
  /** Render labeled group headers between field groups in the autocomplete. */
  showGroupHeaders?: boolean;
}

export const findGlobalControl = (
  schema: StructuredSearchSchema,
  type: GlobalSearchControlType,
): GlobalSearchControl | undefined => schema.globalControls.find(control => control.type === type);

export const schemaHasGlobalControl = (
  schema: StructuredSearchSchema,
  type: GlobalSearchControlType,
): boolean => findGlobalControl(schema, type) !== undefined;

export const getFilterDefinitionByKey = (
  schema: StructuredSearchSchema,
  key: string | undefined,
): FilterDefinition | undefined =>
  key === undefined ? undefined : schema.filters.find(filter => filter.key === key);

/** The single prompt-typed filter for a schema, if any. */
export const getPromptFilterDefinition = (
  schema: StructuredSearchSchema,
): FilterDefinition | undefined =>
  schema.filters.find(filter => filter.type === FilterValueType.Prompt);

export type { FilterValue };
